import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Endpoints que NUNCA devem disparar renovação (evita laço infinito).
// `/auth/me` fica fora da lista de propósito: com o access token expirado e o
// refresh válido, a sessão precisa ser renovada em vez de cair no login.
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

// Renovação single-flight: várias requisições podem receber 401 ao mesmo tempo
// e o refresh token é de uso único, então todas compartilham a MESMA promise.
let refreshPromise = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post("/auth/refresh").finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !NO_REFRESH.some((path) => url.includes(path))
    ) {
      original._retry = true;
      try {
        await refreshSession();
        return api(original);
      } catch (refreshError) {
        // 5xx no refresh é transitório (ex.: índice de rotação indisponível):
        // devolve o erro original sem derrubar a sessão do usuário.
        const status = refreshError?.response?.status || 500;
        // Outra aba pode ter renovado primeiro (o refresh é de uso único e os
        // cookies são compartilhados): com os cookies novos dela, a sessão segue.
        if (status === 401) {
          await new Promise((r) => setTimeout(r, 400));
          try {
            return await api(original);
          } catch {
            /* sessão realmente expirou */
          }
        }
        // Em `/auth/me` quem decide é o AuthContext, e na tela de login um
        // redirect aqui viraria laço de navegação.
        const silent =
          status >= 500 ||
          url.includes("/auth/me") ||
          window.location.pathname.startsWith("/login") ||
          window.location.pathname.startsWith("/wallboard");
        if (!silent) {
          // Volta para a mesma tela depois de entrar de novo.
          const atual = window.location.pathname + window.location.search;
          window.location.href = `/login?next=${encodeURIComponent(atual)}`;
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

// ── Cache curto para listas de configuração quase estáticas ───────────────
// Só entram coleções que praticamente não mudam durante o expediente. Nunca
// dado transacional (serviços, status, chat, valores). O cache serve para
// pintar a tela na hora; qualquer escrita nessas rotas o invalida, e voltar o
// foco na aba limpa tudo — então não existe janela de dado velho relevante.
const CACHEABLE = ["/custom-fields", "/cancel-reasons", "/branding", "/tags"];
const TTL_MS = 60_000;
const listCache = new Map();

export function clearListCache(prefix) {
  if (!prefix) {
    listCache.clear();
    return;
  }
  for (const key of listCache.keys()) {
    if (key.startsWith(prefix)) listCache.delete(key);
  }
}

/** GET com cache curto + revalidação em background (só rotas de CACHEABLE). */
export async function getCachedList(path) {
  if (!CACHEABLE.some((p) => path.startsWith(p))) {
    const { data } = await api.get(path);
    return data;
  }
  const hit = listCache.get(path);
  if (hit && Date.now() - hit.at < TTL_MS) {
    api
      .get(path)
      .then(({ data }) => listCache.set(path, { at: Date.now(), data }))
      .catch(() => {});
    return hit.data;
  }
  const { data } = await api.get(path);
  listCache.set(path, { at: Date.now(), data });
  return data;
}

// Qualquer escrita numa rota cacheada derruba o cache dela na hora.
api.interceptors.response.use((res) => {
  const method = (res.config?.method || "get").toLowerCase();
  if (method !== "get") {
    const url = res.config?.url || "";
    const hit = CACHEABLE.find((p) => url.startsWith(p));
    if (hit) clearListCache(hit);
  }
  return res;
});

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") clearListCache();
  });
}

// Bloqueio por permissão/plano em carregamento de tela (GET): a maioria das
// listas engolia o erro e o usuário só via a tela vazia, sem saber o motivo.
// Só o GET dispara toast aqui — nas ações, quem avisa é a própria tela.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const metodo = (error.config?.method || "get").toLowerCase();
    if (
      metodo === "get" &&
      !error.config?.silenciarBloqueio &&
      (status === 403 || status === 404) &&
      typeof detail === "string" &&
      (detail.startsWith("Bloqueado:") ||
        detail.startsWith("Este recurso não está incluído") ||
        detail.startsWith("Seu usuário não tem um perfil"))
    ) {
      toast.error(detail, { id: `bloqueio-${detail}` });
    }
    return Promise.reject(error);
  },
);

export function formatApiError(detail, fallback = "Algo deu errado. Tente novamente.") {
  if (detail == null) return fallback;
  // Alguns pontos passam o erro do axios inteiro em vez do `detail`; sem isso o
  // usuário via "AxiosError: Request failed with status code 424".
  if (detail?.isAxiosError || detail?.response) {
    const interno = detail.response?.data?.detail ?? detail.response?.data?.message ?? null;
    return formatApiError(interno, fallback);
  }
  if (typeof detail === "string") {
    // Erros gerados pela borda (Cloudflare/ingress) vêm em inglês e não têm
    // valor para o usuário. Nesses casos usamos a mensagem local.
    if (isEdgeErrorMessage(detail)) return fallback;
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e?.msg ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ") || fallback;
  if (detail?.msg) return detail.msg;
  // Erros estruturados do backend ({ code, message, ... }) — sem isso o usuário
  // via "[object Object]".
  if (typeof detail?.message === "string") return detail.message;
  if (typeof detail?.detail === "string") return detail.detail;
  return typeof detail === "object" ? fallback : String(detail);
}

/** Detecta texto de erro de proxy/CDN (sempre em inglês) para não exibi-lo. */
const EDGE_ERROR_MARKERS = [
  "origin web server",
  "bad gateway",
  "cloudflare",
  "gateway time-out",
  "gateway timeout",
  "service temporarily unavailable",
  "service unavailable",
];

function isEdgeErrorMessage(text) {
  const t = String(text).toLowerCase();
  return EDGE_ERROR_MARKERS.some((m) => t.includes(m));
}

/**
 * Mensagem amigável a partir de um erro do axios. Prefira esta função na UI:
 * trata payloads da borda (Cloudflare), timeout e queda de rede em português.
 */
export function friendlyError(err, fallback = "Algo deu errado. Tente novamente.") {
  const res = err?.response;
  if (!res) {
    if (err?.code === "ECONNABORTED") {
      return "A consulta demorou demais e foi interrompida. Tente novamente.";
    }
    return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
  }
  const data = res.data;
  // Corpo de erro do Cloudflare/ingress: nunca mostrar ao usuário.
  if (data && typeof data === "object" && (data.cloudflare_error || data.ray_id)) {
    return "O serviço está temporariamente indisponível. Tente novamente em instantes.";
  }
  if (res.status >= 502 && res.status <= 504) {
    return "O serviço está temporariamente indisponível. Tente novamente em instantes.";
  }
  if (typeof data === "string" && data.trim().startsWith("<")) {
    return "O serviço está temporariamente indisponível. Tente novamente em instantes.";
  }
  return formatApiError(data?.detail, fallback);
}

export default api;
